import { format } from 'date-fns';
import { SortDirection } from '../../shared/utils/constants';
import { NumericKeyOf } from '../../shared/types';

export const formatReportPayloadDate = (
  inputDate: number,
  dateFormat = 'yyyy-MM-dd HH:mm',
) => {
  return format(new Date(inputDate), dateFormat);
};

export const sortReportByDate = <T>(
  data: T[],
  dateKey: keyof T,
  sortDirection: SortDirection,
) => {
  return data.slice().sort((a, b) => {
    const aDateString = a[dateKey] as string;
    const bDateString = b[dateKey] as string;

    if (!aDateString && !bDateString) {
      return 0;
    }

    if (!aDateString) {
      return 1;
    }

    if (!bDateString) {
      return -1;
    }
    const aDate = new Date(aDateString);
    const bDate = new Date(bDateString);

    return sortDirection === SortDirection.ASC
      ? aDate.getTime() - bDate.getTime()
      : bDate.getTime() - aDate.getTime();
  });
};

export const sortReportByNumericValue = <T>(
  data: T[],
  numericKey: NumericKeyOf<T>,
  sortDirection: SortDirection,
) => {
  return data.slice().sort((a, b) => {
    const aValue = a[numericKey];
    const bValue = b[numericKey];

    if (!aValue && !bValue) {
      return 0;
    }

    if (!aValue) {
      return 1;
    }

    if (!bValue) {
      return -1;
    }
    return sortDirection === SortDirection.ASC
      ? Number(aValue) - Number(bValue)
      : Number(bValue) - Number(aValue);
  });
};

export const sortReportByTime = <T>(
  data: T[],
  timeKey: keyof T,
  sortDirection: SortDirection,
) => {
  return data.slice().sort((a, b) => {
    const aTimeString = a[timeKey] as string;
    const bTimeString = b[timeKey] as string;

    if (!aTimeString && !bTimeString) {
      return 0;
    }

    if (!aTimeString) {
      return 1;
    }

    if (!bTimeString) {
      return -1;
    }

    const [aHours, aMinutes] = aTimeString.split(':').map(Number);
    const [bHours, bMinutes] = bTimeString.split(':').map(Number);

    const aDate = new Date();
    aDate.setHours(aHours, aMinutes, 0, 0);

    const bDate = new Date();
    bDate.setHours(bHours, bMinutes, 0, 0);

    return sortDirection === SortDirection.ASC
      ? aDate.getTime() - bDate.getTime()
      : bDate.getTime() - aDate.getTime();
  });
};

export const sortReportByName = <T>(
  data: T[],
  nameKey: keyof T,
  sortDirection: SortDirection,
) => {
  return data.slice().sort((a, b) => {
    const aName = a[nameKey] as string;
    const bName = b[nameKey] as string;

    if (!aName && !bName) {
      return 0;
    }

    if (!aName) {
      return 1;
    }

    if (!bName) {
      return -1;
    }

    return sortDirection === SortDirection.ASC
      ? aName.localeCompare(bName, undefined, { sensitivity: 'base' })
      : bName.localeCompare(aName, undefined, { sensitivity: 'base' });
  });
};
